<template>
  <main class="mypage-redesign">
    <!-- 메인 콘텐츠 영역 -->
    <div class="mypage-container">
      <!-- MypageSidebar 컴포넌트 -->
      <MypageSidebar />

      <!-- 메인 콘텐츠 -->
      <div class="main-content">
        <!-- 나의 리뷰 섹션 -->
        <section class="review-section">
          <div class="section-header">
            <div>
              <h2 class="section-title">나의 리뷰</h2>
              <p class="section-description">작성한 리뷰를 관리하실 수 있습니다</p>
            </div>
          </div>

          <!-- 리뷰 리스트 -->
          <div v-if="myReviewList.length > 0" class="review-list">
            <div v-for="item in myReviewList" :key="item.oid" class="review-card">
              <div class="product-info">
                <div class="product-details">
                  <p v-if="new Date(item.orderDateTime).getFullYear() >= 2000" class="purchase-date">구매일자 : {{ formDate(item.orderDateTime) }}</p>
                  <h3 class="product-title">{{ item.itemTitle }}</h3>
                </div>
              </div>
              <div class="review-info">
                <p class="review-title">{{ item.reviewTitle }}</p>
                <div class="review-actions">
                  <button class="action-button primary" @click="openModal(item)">수정</button>
                </div>
              </div>
            </div>

            <!-- 페이지네이션 -->
            <pagination v-if="pageInfo.page" :pageInfo="pageInfo" :page="page" :size="size" @updatePage="updatePage" />
          </div>

          <!-- 빈 상태 -->
          <div v-else class="empty-state">작성한 리뷰가 없습니다.</div>
        </section>
      </div>
    </div>

    <!-- 리뷰 작성/수정 모달 -->
    <ReviewWriteComponent v-if="modalVisible" @close="modalVisible = false" :content="modalData" />
  </main>
</template>

<script setup>
import axios from "axios";
import store from "@/store";
import { onMounted, ref } from "vue";
import { formDate } from "@/assets/js/util.js";
import MypageSidebar from "@/components/MypageSidebar.vue";
import Pagination from "@/components/Pagination.vue";
import ReviewWriteComponent from "@/components/ReviewWriteComponent.vue";

const myReviewList = ref([]);

const memberIdx = store.state.account.id;

const getmyReviewList = async (num) => {
  const pageValue = num !== undefined ? 1 : page.value;

  try {
    const res = await axios.get(`/new_api/review/list?page=${pageValue}&size=${size.value}&memberIdx=${memberIdx}`);

    myReviewList.value = res.data.data.reviewList;
    pageInfo.value = res.data.data.pageInfo;
  } catch (error) {
    console.error(error);
  }
};

onMounted(async () => {
  await getmyReviewList();
});

const pageInfo = ref({});

const page = ref(1);
const size = ref(10);

const updatePage = (num) => {
  page.value = num;
  getmyReviewList();
};

const modalVisible = ref(false);
const modalData = ref(null);

const openModal = (content) => {
  modalData.value = content;
  modalVisible.value = true;
};
</script>

<style scoped lang="scss">
/* ========================================
   Variables & Reset
   ======================================== */
.mypage-redesign {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 0;
  font-family: "Pretendard", "Malgun Gothic", sans-serif;
}

/* ========================================
   Main Container
   ======================================== */
.mypage-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  display: grid;
  grid-template-columns: 230px 1fr;
  gap: 40px;

  @media (max-width: 1023px) {
    grid-template-columns: 240px 1fr;
    gap: 32px;
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    padding: 20px 16px 40px;
  }
}

/* ========================================
   Main Content
   ======================================== */
.main-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* ========================================
   Review Section
   ======================================== */
.review-section {
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 767px) {
    padding: 24px 16px;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #222;
  margin: 0 0 8px 0;

  @media (max-width: 767px) {
    font-size: 20px;
  }
}

.section-description {
  font-size: 14px;
  color: #888;
  margin: 0;
}

/* ========================================
   Review List
   ======================================== */
.review-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.review-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #dee0e3;
  border-radius: 12px;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 20px;
  }
}

/* ========================================
   Product Info
   ======================================== */
.product-info {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.product-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.purchase-date {
  font-size: 14px;
  color: #888;
  margin: 0;
}

.product-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin: 0;
  line-height: 1.5;

  @media (max-width: 767px) {
    font-size: 15px;
  }
}

/* ========================================
   Review Info
   ======================================== */
.review-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;

  @media (max-width: 767px) {
    align-items: stretch;
  }
}

.review-title {
  font-size: 15px;
  color: #555961;
  margin: 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 767px) {
    font-size: 14px;
  }
}

.review-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    width: 100%;
  }
}

.action-button {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &.primary {
    background: #0c3388;
    color: #ffffff;

    &:hover {
      background: #092968;
    }
  }

  &.secondary {
    background: #ffffff;
    color: #0c3388;
    border: 1px solid #d2d6dc;

    &:hover {
      background: #f7f7f8;
    }
  }

  &:focus-visible {
    outline: 2px solid #0c3388;
    outline-offset: 2px;
  }

  @media (max-width: 767px) {
    flex: 1;
  }
}

/* ========================================
   Empty State
   ======================================== */
.empty-state {
  padding: 60px 24px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 12px;
}

/* ========================================
   Focus Styles
   ======================================== */
*:focus-visible {
  outline: 2px solid #0c3388;
  outline-offset: 2px;
}
</style>
